import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tour } from '@/mocks/tours';

interface ToursByCitySectionProps {
  sectionId: string;
  title: string;
  subtitle: string;
  tours: Tour[];
  bgClass: string;
}

export default function ToursByCitySection({
  sectionId,
  title,
  subtitle,
  tours,
  bgClass,
}: ToursByCitySectionProps) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isDe = i18n.language === 'de';
  const [, setCurrencyVersion] = useState(0);

useEffect(() => {
  const handleCurrencyChange = () => {
    setCurrencyVersion((v) => v + 1);
  };

  window.addEventListener('currencychange', handleCurrencyChange);

  return () => {
    window.removeEventListener('currencychange', handleCurrencyChange);
  };
}, []);

  const getDurationBadge = (type: string) => {
  switch (type) {
    case 'half_day':
      return isDe ? 'Halbtagestour' : 'Half-Day Tour';
    case 'full_day':
      return isDe ? 'Tagesausflug' : 'Day Trip';
    case 'multi_day':
      return isDe ? 'Mehrtägig' : 'Multi-Day';
    default:
      return type;
  }
};

  const getCurrencySymbol = () => {
    const c = (window as any).__CURRENCY__;
    return c ? c.symbol : '€';
  };

  const getConvertedPrice = (price: number) => {
    const c = (window as any).__CURRENCY__;
    if (!c) return price;
    return Math.round(price * c.rate);
  };

  return (
    <section id={sectionId} className={`${bgClass} py-10 md:py-14 lg:py-20`}>
      <div className="w-full px-4 md:px-6 lg:px-12 xl:px-20">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            {title}
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Tours Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {tours.map((tour) => (
            <div
              key={tour.id}
              className="group bg-background-50 rounded-sm overflow-hidden border border-background-200 hover:border-accent-300 transition-all duration-300 hover:shadow-md"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={tour.image_url}
                  alt={isDe ? tour.title_de : tour.title_en}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute top-3 right-3">
                  <span className="inline-block px-2.5 py-1 bg-primary-800/90 text-background-50 text-xs rounded-sm font-medium">
                    {getDurationBadge(tour.duration_type)}
                  </span>
                </div>
                {/* Rating badge */}
                <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-background-50/90 px-2 py-1 rounded-sm text-xs">
                  <i className="ri-star-fill text-accent-500 text-xs" />
                  <span className="font-semibold text-foreground-800">{tour.rating}</span>
                  <span className="text-foreground-500">({tour.reviews_count})</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 line-clamp-2 leading-tight">
                  {isDe ? tour.title_de : tour.title_en}
                </h3>
                <p className="text-foreground-500 text-sm mb-4 line-clamp-2 leading-relaxed">
                  {isDe ? tour.description_de : tour.description_en}
                </p>

                {/* Price & CTA */}
                <div className="flex items-center justify-between pt-3 border-t border-background-200">
                  <div>
                    <span className="text-xs text-foreground-500">{isDe ? 'Ab' : 'From'}</span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-bold text-accent-600">
                        {getCurrencySymbol()}{getConvertedPrice(tour.price)}
                      </span>
                      <span className="text-xs text-foreground-500">{isDe ? 'pro Person' : 'per person'}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/tour/${tour.id}`)}
                    className="px-4 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {isDe ? 'Details ansehen' : 'See Details'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}